// As we use the example from Embeddable where they use any
// we will do the same here.
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createElement,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Alert } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiErrorAlert } from '../ApiErrorAlert';
import { useGetDashboardToken } from '@/api/REST/dashboards';
import { useGetEmbeddableAppToken } from '@/api/REST/embeddableAppToken';
import { isApiError } from '@/api/REST/errors';
import { useLocaleStore } from '@/stores/locale';

interface EmbeddableProps<T = Record<string, unknown>> {
  tokenResult: Pick<
    UseQueryResult<string | undefined>,
    'data' | 'isLoading' | 'error'
  >;
  variables?: T;
  onComponentsLoad?: (e: any) => void;
  onVariablesChange?: Dispatch<SetStateAction<T>>;
  onEmbeddableError?: (error: any) => void;
  clientContext?: Record<string, unknown>;
}

interface EmbeddableDashboardProps<T = Record<string, unknown>> extends Omit<
  EmbeddableProps<T>,
  'tokenResult'
> {
  id: number;
}

interface EmbeddableChartProps<T = Record<string, unknown>> extends Omit<
  EmbeddableProps<T>,
  'tokenResult'
> {
  type: string;
}

type Detail = {
  variableName: string;
  newValue: unknown;
};

function Embeddable<T = Record<string, unknown>>({
  tokenResult,
  variables,
  onVariablesChange,
  onComponentsLoad,
  onEmbeddableError,
  clientContext,
}: EmbeddableProps<T>) {
  const ref = useRef<any>(null);
  const { t } = useTranslation('charts');
  const { data: token, isLoading, error } = tokenResult;
  const { lang } = useLocaleStore();

  // Merge default client context (language) with any additional context from consumer
  const mergedClientContext = {
    language: lang,
    ...clientContext,
  };

  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<Error | null>(null);

  useEffect(() => {
    if (customElements.get('em-beddable')) {
      setScriptLoaded(true);
      return;
    }
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://embed.embeddable.com/js/v1/?region=EU"]',
    );
    if (existingScript) {
      const onLoad = () => setScriptLoaded(true);
      const onError = () => {
        setScriptError(new Error('Failed to load Embeddable script'));
        setScriptLoaded(false);
      };

      existingScript.addEventListener('load', onLoad, { once: true });
      existingScript.addEventListener('error', onError, { once: true });

      return () => {
        existingScript.removeEventListener('load', onLoad);
        existingScript.removeEventListener('error', onError);
      };
    }

    const script = document.createElement('script');
    script.src = 'https://embed.embeddable.com/js/v1/?region=EU';
    script.type = 'module';

    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      setScriptError(new Error('Failed to load Embeddable script'));
      setScriptLoaded(false);
    };

    document.body.appendChild(script);
  }, []);

  const handleVariableChange = useCallback(
    (e: { detail: Detail[] }) => {
      if (onVariablesChange) {
        const deltaMapped = Object.fromEntries(
          e.detail.map((c) => [c.variableName, c.newValue]),
        ) as Partial<T>;
        onVariablesChange((prev) => ({ ...prev, ...deltaMapped }));
      }
    },
    [onVariablesChange],
  );

  const handleEmbeddableError = useCallback(
    (e: any) => {
      if (onEmbeddableError) {
        onEmbeddableError(e.detail);
      }
    },
    [onEmbeddableError],
  );

  useEffect(() => {
    const element = ref.current;
    if (element) {
      element.addEventListener('variablesChange', handleVariableChange);
      if (onComponentsLoad) {
        element.addEventListener('componentsLoad', onComponentsLoad);
      }
      element.addEventListener('embeddableError', handleEmbeddableError);

      return () => {
        element.removeEventListener('variablesChange', handleVariableChange);
        if (onComponentsLoad) {
          element.removeEventListener('componentsLoad', onComponentsLoad);
        }
        element.removeEventListener('embeddableError', handleEmbeddableError);
      };
    }
  }, [handleVariableChange, handleEmbeddableError, onComponentsLoad]);

  if (isLoading || !scriptLoaded) {
    return t('genericFeedback.LoadingData');
  }

  if (scriptError || !token) {
    // 403/5xx are handled globally (toast) by the axios interceptor and are not ApiErrors,
    // so they fall through to the generic Alert below. Real API errors (400, 422, etc.) are
    // shown here because the toast disappears and the user would otherwise have no indication
    // of why there is no data on the page.
    if (isApiError(error)) {
      return <ApiErrorAlert error={error} />;
    }
    return (
      <Alert severity="error">
        {t('genericFeedback.ErrorLoadingData', {
          error: scriptError?.message ?? '',
        })}
      </Alert>
    );
  }

  return createElement('em-beddable', {
    ref,
    token,
    'client-context': JSON.stringify(mergedClientContext),
    variables: JSON.stringify(variables),
  });
}

export function EmbeddableDashboard<T = Record<string, unknown>>({
  id,
  ...restProps
}: EmbeddableDashboardProps<T>) {
  const tokenResult = useGetDashboardToken(id);

  return <Embeddable<T> tokenResult={tokenResult} {...restProps} />;
}

export function EmbeddableChart<T = Record<string, unknown>>({
  type,
  ...restProps
}: EmbeddableChartProps<T>) {
  const tokenResult = useGetEmbeddableAppToken(type);

  return <Embeddable<T> tokenResult={tokenResult} {...restProps} />;
}
